// Pure helper that walks a list of material ids and applies a
// per-row "set is_active" call, accumulating a `{ updated, failed }`
// summary. Extracted from `useManufacturingInventoryCleanup.ts` so the
// aggregation contract (fail-soft, per-row try, deterministic count)
// can be unit-tested in the node-vitest env without round-tripping
// the manufacturing client / Supabase.
//
// Caller injects the per-row updater. Each updater returns the same
// `{ ok: true } | { ok: false; error }` shape the rest of the
// cleanup hook already speaks. A thrown updater is treated as a
// `failed` row -- we don't want one network blip to abort the
// remaining rows in a 100-row bulk operation.

export type RowUpdaterResult = { ok: true } | { ok: false; error: string };

export type BulkActiveFlagSummary = {
  updated: number;
  failed: number;
};

export type RowUpdater = (
  materialId: string,
  isActive: boolean
) => Promise<RowUpdaterResult>;

export async function applyBulkActiveFlag(
  materialIds: ReadonlyArray<string>,
  isActive: boolean,
  updater: RowUpdater
): Promise<BulkActiveFlagSummary> {
  let updated = 0;
  let failed = 0;
  for (const id of materialIds) {
    let result: RowUpdaterResult;
    try {
      result = await updater(id, isActive);
    } catch {
      failed += 1;
      continue;
    }
    if (result.ok) updated += 1;
    else failed += 1;
  }
  return { updated, failed };
}
